import React from "react";
import { TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";

const rate = (c) => (c.incoming ? Math.round((c.resolved / c.incoming) * 1000) / 10 : null);
const RateSpan = ({ v, testId }) => (
  v === null || v === undefined
    ? <span className="text-slate-400" data-testid={testId} title="Χωρίς εισερχόμενες το έτος αυτό">—</span>
    : <span className={`font-bold ${v >= 100 ? "text-emerald-600" : v >= 80 ? "text-amber-600" : "text-red-600"}`} data-testid={testId}>{v}%</span>
);
const Cells = ({ c, prefix }) => (
  <>
    <td className="py-2 px-2 text-right text-blue-700">{c.incoming}</td>
    <td className="py-2 px-2 text-right text-emerald-700">{c.resolved}</td>
    <td className="py-2 px-2 text-right border-r border-slate-200"><RateSpan v={c.ct_rate ?? rate(c)} testId={prefix} /></td>
  </>
);

/** CT Rate ΜΟΝΟ Νέας Τακτικής (ΑΓΩΓΗ/ΚΛΗΣΗ/ΠΑΡΕΜΒΑΣΕΙΣ σε ΕΝΟΧΙΚΟ…ΕΙΔΙΚΟ ΕΜΠΟΡΙΚΟ) ανά έτος × Μον/Πολ/Σύνολο. */
export const CtRateNewRegularCard = React.forwardRef(({ data }, ref) => {
  const rows = data?.rows || [];
  if (!rows.length) return null;
  const sum = (k) => rows.reduce((a, r) => ({ incoming: a.incoming + r[k].incoming, resolved: a.resolved + r[k].resolved, unscheduled: a.unscheduled + r[k].unscheduled }), { incoming: 0, resolved: 0, unscheduled: 0 });
  const totals = { mono: sum("mono"), poly: sum("poly"), total: sum("total") };
  return (
    <Card ref={ref} data-testid="ct-rate-new-regular-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="w-5 h-5 text-emerald-700" />
          CT Rate — Νέα Τακτική Διαδικασία (Μονομελές · Πολυμελές)
        </CardTitle>
        <p className="text-sm text-slate-500 mt-1">
          Μόνο δικόγραφα <b>{(data.criteria?.case_types || []).join(" · ")}</b> στους τομείς <b>{(data.criteria?.subjects || []).join(" / ")}</b>
          {" "}— με ή χωρίς ν.5221/2025, όλα τα έτη. Ίδιος ορισμός: Εκκαθαρισμένες = έλαβαν δικάσιμο εντός του έτους / Εισερχόμενες του έτους.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ct-rate-new-regular-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2 px-2 font-semibold text-slate-700" rowSpan={2}>Έτος</th>
                <th className="text-center py-1 px-2 font-semibold text-slate-700 border-r border-slate-200" colSpan={3}>Μονομελές</th>
                <th className="text-center py-1 px-2 font-semibold text-slate-700 border-r border-slate-200" colSpan={3}>Πολυμελές</th>
                <th className="text-center py-1 px-2 font-semibold text-slate-900 border-r border-slate-200" colSpan={3}>Σύνολο</th>
                <th className="text-right py-2 px-2 font-semibold text-slate-700" rowSpan={2}>Χωρίς δικάσιμο<br /><span className="font-normal text-xs text-slate-500">από τις εισερχόμενες</span></th>
              </tr>
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                {[0, 1, 2].map((i) => (
                  <React.Fragment key={i}>
                    <th className="text-right py-1 px-2 font-medium">Εισερχ.</th>
                    <th className="text-right py-1 px-2 font-medium">Εκκαθ.</th>
                    <th className="text-right py-1 px-2 font-medium border-r border-slate-200">CT</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year} className="border-b border-slate-100" data-testid={`ct-nt-row-${r.year}`}>
                  <td className="py-2 px-2 font-medium">{r.year}</td>
                  <Cells c={r.mono} prefix={`ct-nt-mono-${r.year}`} />
                  <Cells c={r.poly} prefix={`ct-nt-poly-${r.year}`} />
                  <Cells c={r.total} prefix={`ct-nt-total-${r.year}`} />
                  <td className="py-2 px-2 text-right text-slate-500">{r.total.unscheduled}</td>
                </tr>
              ))}
              {rows.length > 1 && (
                <tr className="bg-slate-50 font-semibold" data-testid="ct-nt-total-row">
                  <td className="py-2 px-2">Σύνολο</td>
                  <Cells c={{ ...totals.mono, ct_rate: rate(totals.mono) }} prefix="ct-nt-mono-all" />
                  <Cells c={{ ...totals.poly, ct_rate: rate(totals.poly) }} prefix="ct-nt-poly-all" />
                  <Cells c={{ ...totals.total, ct_rate: rate(totals.total) }} prefix="ct-nt-total-all" />
                  <td className="py-2 px-2 text-right text-slate-700">{totals.total.unscheduled}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
});
CtRateNewRegularCard.displayName = "CtRateNewRegularCard";
